"use client";

import { useEffect, useMemo } from "react";

import { useIntegrations } from "@/features/integrations/hooks/useIntegrations";
import { TriggerAutocomplete } from "@/features/workflows/components/TriggerAutocomplete";
import { useTriggerSchemas } from "@/features/workflows/triggers/hooks/useTriggerSchemas";
import {
  createDefaultTriggerConfig,
  getTriggerHandler,
} from "@/features/workflows/triggers/registry";
import type { TriggerConfig } from "@/features/workflows/triggers/types";
import { findTriggerSchema } from "@/features/workflows/triggers/utils";

interface TriggerConfigFormProps {
  selectedTrigger: string;
  triggerConfig: TriggerConfig;
  onTriggerChange: (trigger: string) => void;
  onConfigChange: (config: TriggerConfig) => void;
}

export function TriggerConfigForm({
  selectedTrigger,
  triggerConfig,
  onTriggerChange,
  onConfigChange,
}: TriggerConfigFormProps) {
  const { data: triggerSchemas, isLoading: schemasLoading } =
    useTriggerSchemas();
  const { integrations } = useIntegrations();

  const integrationStatusMap = useMemo(() => {
    const map = new Map<string, boolean>();
    integrations.forEach((integration) => {
      map.set(integration.id, integration.status === "connected");
    });
    return map;
  }, [integrations]);

  const selectedSchema = findTriggerSchema(triggerSchemas, selectedTrigger);
  const normalizedSlug = selectedSchema?.slug ?? selectedTrigger;

  const handleTriggerSelect = (trigger: string | null) => {
    if (!trigger) {
      onTriggerChange("");
      return;
    }

    onTriggerChange(trigger);

    const schema = triggerSchemas?.find((s) => s.slug === trigger);
    const defaultConfig = createDefaultTriggerConfig(trigger);

    if (defaultConfig) {
      onConfigChange({
        ...defaultConfig,
        integration_id: schema?.integration_id,
        trigger_slug: schema?.slug,
      });
    } else {
      onConfigChange({
        type: "manual",
        enabled: true,
      });
    }
  };

  useEffect(() => {
    if (!normalizedSlug || schemasLoading) return;

    const handler = getTriggerHandler(normalizedSlug);
    if (!handler) return;

    const isValidTriggerConfig =
      triggerConfig.type !== "schedule" && triggerConfig.type !== "manual";

    if (!isValidTriggerConfig) {
      const defaultConfig = createDefaultTriggerConfig(normalizedSlug);
      if (defaultConfig) {
        onConfigChange(defaultConfig);
      }
    }
  }, [normalizedSlug, triggerConfig.type, schemasLoading, onConfigChange]);

  const handler = getTriggerHandler(normalizedSlug);
  const SettingsComponent = handler?.SettingsComponent;

  // When the trigger's integration isn't connected, the settings can't load
  // (their option fetches need the connection) and the connect call-to-action
  // lives in the modal's top banner — so don't render the settings/inline
  // prompt here. Resolved the same way as that banner (integration of the
  // selected trigger's schema) so the two never disagree.
  const triggerIntegration = selectedSchema?.integration_id
    ? integrations.find((i) => i.id === selectedSchema.integration_id)
    : undefined;
  const triggerIntegrationConnected =
    !triggerIntegration || triggerIntegration.status === "connected";

  return (
    <div className="w-full space-y-4">
      <TriggerAutocomplete
        selectedTrigger={selectedTrigger}
        onTriggerChange={handleTriggerSelect}
        triggerSchemas={triggerSchemas}
        isLoading={schemasLoading}
        integrationStatusMap={integrationStatusMap}
      />

      {SettingsComponent && triggerIntegrationConnected && (
        <SettingsComponent
          triggerConfig={{
            ...triggerConfig,
            trigger_name:
              normalizedSlug ||
              (triggerConfig as { trigger_name?: string }).trigger_name,
          }}
          onConfigChange={onConfigChange}
        />
      )}
    </div>
  );
}
